import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { appConfig } from '../config/appConfig';

export interface RetrievalSettings {
  numberOfResults: number;
  searchType: 'HYBRID' | 'SEMANTIC';
  scoreThreshold: number;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedKB: string;
  onKBChange: (kbId: string) => void;
  settings: RetrievalSettings;
  onSettingsChange: (settings: RetrievalSettings) => void;
  customSystemPrompt: string;
  onCustomSystemPromptChange: (prompt: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  onClose, 
  selectedKB, 
  onKBChange,
  settings,
  onSettingsChange,
  customSystemPrompt,
  onCustomSystemPromptChange
}) => {
  const knowledgeBases = appConfig.ai.knowledgeBases || [];
  const [tempSettings, setTempSettings] = useState(settings);
  const [tempSystemPrompt, setTempSystemPrompt] = useState(customSystemPrompt);
  const [kbOpen, setKbOpen] = useState(true);
  const [retrievalOpen, setRetrievalOpen] = useState(false);
  const [systemPromptOpen, setSystemPromptOpen] = useState(false);
  const [fixedInstructionsOpen, setFixedInstructionsOpen] = useState(false);
  const [showCurrentPrompt, setShowCurrentPrompt] = useState(false);
  const [showKbInfo, setShowKbInfo] = useState(false);
  const [showRetrievalInfo, setShowRetrievalInfo] = useState(false);
  const [showSystemPromptInfo, setShowSystemPromptInfo] = useState(false);
  const [showChunksInfo, setShowChunksInfo] = useState(false);
  const [showSearchTypeInfo, setShowSearchTypeInfo] = useState(false);
  const [showThresholdInfo, setShowThresholdInfo] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  const kbInfoRef = useRef<HTMLButtonElement>(null);
  const retrievalInfoRef = useRef<HTMLButtonElement>(null);
  const systemPromptInfoRef = useRef<HTMLButtonElement>(null);
  const chunksInfoRef = useRef<HTMLButtonElement>(null);
  const searchTypeInfoRef = useRef<HTMLButtonElement>(null);
  const thresholdInfoRef = useRef<HTMLButtonElement>(null);
  const kbTooltipRef = useRef<HTMLDivElement>(null);
  const retrievalTooltipRef = useRef<HTMLDivElement>(null);
  const systemPromptTooltipRef = useRef<HTMLDivElement>(null);
  const chunksTooltipRef = useRef<HTMLDivElement>(null);
  const searchTypeTooltipRef = useRef<HTMLDivElement>(null);
  const thresholdTooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempSettings(settings);
    setTempSystemPrompt(customSystemPrompt);
  }, [settings, customSystemPrompt, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showKbInfo && kbInfoRef.current && !kbInfoRef.current.contains(event.target as Node) &&
          kbTooltipRef.current && !kbTooltipRef.current.contains(event.target as Node)) {
        setShowKbInfo(false);
      }
      if (showRetrievalInfo && retrievalInfoRef.current && !retrievalInfoRef.current.contains(event.target as Node) &&
          retrievalTooltipRef.current && !retrievalTooltipRef.current.contains(event.target as Node)) {
        setShowRetrievalInfo(false);
      }
      if (showSystemPromptInfo && systemPromptInfoRef.current && !systemPromptInfoRef.current.contains(event.target as Node) &&
          systemPromptTooltipRef.current && !systemPromptTooltipRef.current.contains(event.target as Node)) {
        setShowSystemPromptInfo(false);
      }
      if (showChunksInfo && chunksInfoRef.current && !chunksInfoRef.current.contains(event.target as Node) &&
          chunksTooltipRef.current && !chunksTooltipRef.current.contains(event.target as Node)) {
        setShowChunksInfo(false);
      }
      if (showSearchTypeInfo && searchTypeInfoRef.current && !searchTypeInfoRef.current.contains(event.target as Node) &&
          searchTypeTooltipRef.current && !searchTypeTooltipRef.current.contains(event.target as Node)) {
        setShowSearchTypeInfo(false);
      }
      if (showThresholdInfo && thresholdInfoRef.current && !thresholdInfoRef.current.contains(event.target as Node) &&
          thresholdTooltipRef.current && !thresholdTooltipRef.current.contains(event.target as Node)) {
        setShowThresholdInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showKbInfo, showRetrievalInfo, showSystemPromptInfo, showChunksInfo, showSearchTypeInfo, showThresholdInfo]);

  const handleInfoClick = (ref: React.RefObject<HTMLButtonElement | null>, setter: (val: boolean) => void, currentState: boolean) => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top,
        left: rect.right + 8
      });
    }
    setter(!currentState);
  };

  const hasChanges = 
    tempSettings.numberOfResults !== settings.numberOfResults ||
    tempSettings.searchType !== settings.searchType ||
    tempSettings.scoreThreshold !== settings.scoreThreshold ||
    tempSystemPrompt !== customSystemPrompt;

  const handleSave = () => {
    onSettingsChange(tempSettings);
    onCustomSystemPromptChange(tempSystemPrompt);
  };

  const handleReset = () => {
    setTempSystemPrompt('');
  };

  const currentPrompt = tempSystemPrompt || appConfig.ai.systemPrompt;
  const fullPrompt = `${currentPrompt}\n\n${appConfig.ai.systemPromptInstructions}`;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-96 bg-white dark:bg-gray-800 shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Knowledge Base Section */}
            <div className="border-b dark:border-gray-700">
              <button
                onClick={() => setKbOpen(!kbOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Knowledge Base</h3>
                  <button
                    ref={kbInfoRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInfoClick(kbInfoRef, setShowKbInfo, showKbInfo);
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <span className="text-gray-500">{kbOpen ? '▼' : '▶'}</span>
              </button>
              {kbOpen && (
                <div className="px-4 pb-4">
                  <div className="space-y-3">
                  {knowledgeBases.map((kb) => (
                    <label
                      key={kb.id}
                      className="flex items-start gap-3 p-3 rounded-lg border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <input
                        type="radio"
                        name="knowledge-base"
                        value={kb.id}
                        checked={selectedKB === kb.id}
                        onChange={(e) => onKBChange(e.target.value)}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                          {kb.name}
                        </div>
                        {kb.description && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {kb.description}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            </div>

            {/* Retrieval Settings Section */}
            <div className="border-b dark:border-gray-700">
              <button
                onClick={() => setRetrievalOpen(!retrievalOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Retrieval Settings</h3>
                  <button
                    ref={retrievalInfoRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInfoClick(retrievalInfoRef, setShowRetrievalInfo, showRetrievalInfo);
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <span className="text-gray-500">{retrievalOpen ? '▼' : '▶'}</span>
              </button>
              {retrievalOpen && (
                <div className="px-4 pb-4 space-y-4">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                        Number of Chunks (1-20)
                      </label>
                      <button
                        ref={chunksInfoRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInfoClick(chunksInfoRef, setShowChunksInfo, showChunksInfo);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={tempSettings.numberOfResults}
                      onChange={(e) => setTempSettings({ ...tempSettings, numberOfResults: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                        Search Type
                      </label>
                      <button
                        ref={searchTypeInfoRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInfoClick(searchTypeInfoRef, setShowSearchTypeInfo, showSearchTypeInfo);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <select
                      value={tempSettings.searchType}
                      onChange={(e) => setTempSettings({ ...tempSettings, searchType: e.target.value as 'HYBRID' | 'SEMANTIC' })}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="HYBRID">Hybrid</option>
                      <option value="SEMANTIC">Semantic</option>
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                        Score Threshold: {tempSettings.scoreThreshold.toFixed(2)}
                      </label>
                      <button
                        ref={thresholdInfoRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInfoClick(thresholdInfoRef, setShowThresholdInfo, showThresholdInfo);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={tempSettings.scoreThreshold}
                      onChange={(e) => setTempSettings({ ...tempSettings, scoreThreshold: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* System Prompt Section */}
            {appConfig.features.allowCustomSystemPrompt && (
              <div className="border-b dark:border-gray-700">
                <button
                  onClick={() => setSystemPromptOpen(!systemPromptOpen)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">System Prompt</h3>
                    <button
                      ref={systemPromptInfoRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInfoClick(systemPromptInfoRef, setShowSystemPromptInfo, showSystemPromptInfo);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <span className="text-gray-500">{systemPromptOpen ? '▼' : '▶'}</span>
                </button>
                {systemPromptOpen && (
                  <div className="px-4 pb-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Custom Instructions (Optional)
                      </label>
                      <textarea
                        value={tempSystemPrompt}
                        onChange={(e) => setTempSystemPrompt(e.target.value)}
                        placeholder={appConfig.ai.systemPrompt}
                        maxLength={4000}
                        rows={6}
                        className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono resize-none"
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">{tempSystemPrompt.length}/4000</span>
                        <button
                          onClick={handleReset}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Reset to default
                        </button>
                      </div>
                    </div>

                    <div>
                      <button
                        onClick={() => setFixedInstructionsOpen(!fixedInstructionsOpen)}
                        className="w-full flex items-center justify-between text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        <span>Fixed Instructions (Always Applied)</span>
                        <span className="text-gray-500">{fixedInstructionsOpen ? '▼' : '▶'}</span>
                      </button>
                      {fixedInstructionsOpen && (
                        <div className="p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-600 dark:text-gray-400 font-mono">
                          {appConfig.ai.systemPromptInstructions}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => setShowCurrentPrompt(!showCurrentPrompt)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {showCurrentPrompt ? 'Hide' : 'Show'} full prompt
                    </button>
                    {showCurrentPrompt && (
                      <div className="p-3 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap max-h-60 overflow-y-auto">
                        {fullPrompt}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="p-4 border-t dark:border-gray-700">
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`w-full px-4 py-2 rounded font-medium transition-colors ${
                hasChanges
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Tooltips rendered at document body level */}
      {showKbInfo && createPortal(
        <div 
          ref={kbTooltipRef}
          className="fixed z-[100] w-64 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-300"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="absolute -left-2 top-2 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
          Document collections the assistant searches to answer questions.
        </div>,
        document.body
      )}

      {showRetrievalInfo && createPortal(
        <div 
          ref={retrievalTooltipRef}
          className="fixed z-[100] w-64 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-300"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="absolute -left-2 top-2 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
          Control how documents are retrieved from the knowledge base. More chunks provide more context but may include less relevant information. Score threshold filters out low-relevance results.
        </div>,
        document.body
      )}

      {showSystemPromptInfo && createPortal(
        <div 
          ref={systemPromptTooltipRef}
          className="fixed z-[100] w-64 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-300"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="absolute -left-2 top-2 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
          Customize the assistant's behavior and expertise. The system prompt defines how the AI responds and what knowledge it emphasizes. Fixed instructions ensure proper citation formatting.
        </div>,
        document.body
      )}

      {showChunksInfo && createPortal(
        <div 
          ref={chunksTooltipRef}
          className="fixed z-[100] w-64 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-300"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="absolute -left-2 top-2 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
          Number of document chunks retrieved from the knowledge base. More chunks provide broader context but may dilute relevance. Fewer chunks focus on the most relevant information but may miss important details.
        </div>,
        document.body
      )}

      {showSearchTypeInfo && createPortal(
        <div 
          ref={searchTypeTooltipRef}
          className="fixed z-[100] w-64 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-300"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="absolute -left-2 top-2 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
          <strong>Hybrid:</strong> Combines semantic meaning with keyword matching for balanced results.<br/><br/>
          <strong>Semantic:</strong> Uses AI embeddings to find conceptually similar content, even without exact keyword matches.
        </div>,
        document.body
      )}

      {showThresholdInfo && createPortal(
        <div 
          ref={thresholdTooltipRef}
          className="fixed z-[100] w-64 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-300"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="absolute -left-2 top-2 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
          Minimum relevance score (0-1) for retrieved chunks. Higher values filter out less relevant results but may return fewer chunks. A value of 0 includes all results regardless of relevance.
        </div>,
        document.body
      )}
    </>
  );
};
